import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, MenuItem, Select, Typography } from '@mui/material';
import PlanPrompt from './PlanPrompt';
import { DEEP_GREEN } from '../colorTokens';
import { KIND_LABEL, compareVersionStrings, formatDate, formatNumber } from './format';
import { useVersionCatalog } from './useReleaseCadence';

const asEntry = (r) => ({ version: r.version, kind: r.kind, prerelease: !!r.prerelease, smallRollout: !!r.smallRollout, hasRollout: true, rolloutVersion: r.version, hasTests: false, plan: null });

/**
 * Every known version (rollout, test plan or wave plan), newest first. While the catalog loads the picker falls back
 * to the versions that have rollout runs. rolloutVersion is the string to ask the rollout endpoints for.
 */
export const useVersionPicker = (overview, { skipSmall = false } = {}) => {
  const { data, loading } = useVersionCatalog();
  const entries = useMemo(
    () => (data ? data.versions : [...overview.releases].sort((a, b) => compareVersionStrings(b.version, a.version)).map(asEntry)),
    [data, overview]
  );
  const [selected, setSelected] = useState('');
  useEffect(() => {
    if (selected || !entries.length) return;
    const usual = (e) => e.hasRollout && !e.prerelease && !(skipSmall && e.smallRollout);
    setSelected((entries.find(usual) || entries.find((e) => e.hasRollout) || entries[0]).version);
  }, [selected, entries, skipSmall]);

  const select = (version) => {
    const hit = entries.find((e) => e.version === version || e.rolloutVersion === version);
    if (hit) setSelected(hit.version);
  };
  const entry = entries.find((e) => e.version === selected) || null;
  return { entries, entry, select, loading, rolloutVersion: entry && entry.hasRollout ? entry.rolloutVersion : '' };
};

const suffix = (e) => [e.prerelease && 'pre-release', e.smallRollout && 'small', !e.hasRollout && (e.upcoming ? 'upcoming' : 'no rollout data')].filter(Boolean).join(', ');

export const VersionPicker = ({ entries, selected, onChange, loading }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>Version</Typography>
    <Select size="small" value={selected} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 240, borderRadius: '10px' }}>
      {entries.map((e) => (
        <MenuItem key={e.version} value={e.version}>{e.version}{suffix(e) ? ` (${suffix(e)})` : ''}</MenuItem>
      ))}
    </Select>
    {loading && <CircularProgress size={20} />}
  </Box>
);

/** Shown instead of the rollout figures for a version Datadog no longer holds runs for. */
export const NoRolloutNotice = ({ entry, historyStart, onOpenPlans }) => {
  const { plan } = entry;
  const facts = [
    `${KIND_LABEL[entry.kind] || 'Release'} release`,
    plan ? `planned over ${formatNumber(plan.weekends)} ${plan.weekends === 1 ? 'weekend' : 'weekends'}${plan.dateStart ? `, ${formatDate(plan.dateStart)} to ${formatDate(plan.dateEnd || plan.dateStart)}` : ''}, ${formatNumber(plan.instances)} instances` : 'no deployment schedule uploaded',
    entry.hasTests ? 'test results recorded on the Confidence tab' : 'no test plan found'
  ];
  return (
    <PlanPrompt onOpenPlans={plan ? null : onOpenPlans}>
      <Typography sx={{ fontWeight: 600 }}>{entry.version}: {facts.join(' · ')}.</Typography>
      <Typography variant="body2">
        {entry.upcoming
          ? 'This release has not rolled out yet. Rollout waves and readiness appear here once its first instances are upgraded.'
          : `Rollout outcomes are not recorded for this version. Datadog keeps about 30 days of deployment events${historyStart ? `, so the portal's history starts on ${formatDate(historyStart)}` : ''}, and this release was rolled out before that.`}
      </Typography>
      {!plan && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {entry.upcoming
            ? 'Upload the deployment schedule workbook to see which instances are planned in which wave.'
            : 'Upload the deployment schedule workbook to see what was planned for this release.'}
        </Typography>
      )}
    </PlanPrompt>
  );
};
